import {
  AmazonDoorAccessCountSchema,
  AmazonDoorAccessLocationSchema,
  type AmazonDoorAccessLocation,
} from '../schemas/amazonDoorAccess';

// A mid-rollout network: most of the configured locations already let Amazon in, a minority does
// not. Deliberately not a round or a finished pair — the disabled rows are what make the bar, the
// legend and the excluding caption visible in local dev and in every preview env.
//
// Ids 1…9610, synthetic and contiguous, because nothing on this screen renders an id — only how
// many there are. The enabled side takes the first 8412; the disabled side the rest.
const SEED = { enabled: 8412, disabled: 1198 } as const;

// A store rather than two frozen constants: the counter has to actually MOVE when a batch is
// written, or the delta — F2's only receipt, and its whole safety story — cannot be exercised by
// hand under VITE_USE_MOCK_API.
let enabledIds = new Set<number>();
let disabledIds = new Set<number>();

// Exported for tests: module state outlives an `it`, so a suite that writes before it reads would
// otherwise depend on its own declaration order.
export function resetDoorAccessMock(): void {
  enabledIds = new Set(Array.from({ length: SEED.enabled }, (_, i) => i + 1));
  disabledIds = new Set(
    Array.from({ length: SEED.disabled }, (_, i) => SEED.enabled + i + 1),
  );
}

resetDoorAccessMock();

export async function fetchDoorAccessCountMock(enabled: boolean): Promise<number> {
  // Simulate network latency for a realistic dev experience.
  await new Promise((resolve) => setTimeout(resolve, 100));
  const rows = enabled ? enabledIds.size : disabledIds.size;
  // Through the same parse as the real read, so the mock cannot drift from the wire shape.
  const payload = { locations: Array.from({ length: rows }, () => ({})) };
  return AmazonDoorAccessCountSchema.parse(payload).locations.length;
}

// The read by id, over the SAME store as the count and the write, so a lookup in dev reflects a
// wave written by hand a second earlier — which is why this mock is a store and not two constants.
//
// It never 404s and never rejects: an id on neither side answers the default state with
// `updated_at: null`, which is the delivered behaviour and what makes «Sin configuración previa»
// the only signal the screen has.
export async function fetchDoorAccessLocationMock(
  locationId: number,
): Promise<AmazonDoorAccessLocation> {
  await new Promise((resolve) => setTimeout(resolve, 100));
  const configured = enabledIds.has(locationId) || disabledIds.has(locationId);
  const payload = {
    location_id: locationId,
    enabled: enabledIds.has(locationId),
    // A fixed instant, never `new Date()`: the panel consumes this only as the «configured» signal,
    // and a value that moves inside a fixture is a test that fails at midnight.
    updated_at: configured ? '2026-08-18T12:00:00' : null,
  };
  // Through the same parse as the real read, so the mock cannot drift from the wire shape.
  return AmazonDoorAccessLocationSchema.parse(payload);
}

// The upsert the delivered contract describes, side effects included: an id lands on the target
// side whether or not it was on the other one, and an id nobody has configured GETS A ROW.
//
// That last part is deliberate. `AS-043`(i) is an `update_or_create` with no existence check, so a
// numerically valid id that is not a location is persisted silently — a mock that quietly refused
// it would hide from dev the one failure mode this screen cannot detect.
export async function setDoorAccessForLocationsMock(
  locationIds: number[],
  enabled: boolean,
): Promise<void> {
  await new Promise((resolve) => setTimeout(resolve, 100));
  const [target, other] = enabled ? [enabledIds, disabledIds] : [disabledIds, enabledIds];
  for (const id of locationIds) {
    other.delete(id);
    target.add(id);
  }
}
